import React, { useLayoutEffect, useRef, useState } from 'react'
import styled from '~/app/components/styled'
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '~/app/components/common/Button/Button'
import FixedTooltip from '~/app/components/common/Tooltip/FixedTooltip'
import { toKRW } from '~/app/utils/krNumberToString'
import VipDiscountNotice from '~/app/components/common/VipDiscountNotice'
import { Icon_help } from '@yanolja-rnd/ya-com-system-icon'
import { Typography } from '@yanolja-rnd/ya-com-core'

/**
 * CartPaymentButton component
 * author: jaegeunlee
 * description
 */
const DescContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  word-break: keep-all;
  white-space: nowrap;
  .item-count {
    ${Typography('body1').bold};
  }
  .total-price {
    padding-left: 0.8rem;
    ${Typography('subtitle1').bold};
  }
`

const PriceAmountWrapper = styled.div`
  display: flex;
  align-items: center;
`

const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
  word-break: normal;
  white-space: normal;

  .content {
    display: flex;
    align-items: center;
  }
  .text {
    ${Typography('body2').regular};
    color: ${({ theme }) => theme.color.gray600};
  }
  .tooltip-icon {
    margin-left: 0.2rem;
    height: 1.4rem;
    width: 1.4rem;
    cursor: pointer;
    position: relative;
    top: -0.1rem;
  }
  .tooltip-icon svg path {
    fill: ${({ theme }) => theme.color.gray600};
  }
`

const TooltipContent = styled.ul`
  z-index: 101;
  list-style: disc;
  ${Typography('body2.multiline').regular};
  padding-left: 1.2rem;
`

const ButtonWrapper = styled.div`
  margin-top: 1.6rem;
`

interface CartPaymentButtonProps {
  count: number
  trainCheckedCount: number
  price: number
  totalMemberDiscountPriceText: string
  disabled: boolean
  onOrderClick?: () => void
  onDisabledOrderClick?: () => void
}

const CartPaymentButton: React.FC<CartPaymentButtonProps> = ({
  count,
  price,
  totalMemberDiscountPriceText,
  disabled,
  onOrderClick,
  onDisabledOrderClick,
}) => {
  const toolTipContent = useRef(
    <TooltipContent>
      <li>최대 할인이 적용된 금액이에요.</li>
      <li>
        실제 결제 금액은 선착순 쿠폰의 잔여 수량 및 기차 묶음할인 금액 변동에 따라 달라질 수 있어요.
      </li>
    </TooltipContent>
  ).current
  const offsetContainerRef = useRef<HTMLDivElement>(null)
  const [offsetContainerWidth, setOffsetContainerWidth] = useState(
    offsetContainerRef.current?.getBoundingClientRect().width ?? 0
  )
  useLayoutEffect(() => {
    setOffsetContainerWidth(offsetContainerRef.current?.getBoundingClientRect().width ?? 0)
  }, [price])

  return (
    <div>
      <DescContainer>
        <div className="item-count">총 {count}건</div>
        <PriceAmountWrapper>
          <TooltipWrapper>
            <FixedTooltip
              content={toolTipContent}
              includeCloseIcon={false}
              stickyTo={'right'}
              placement={'top'}
              customTooltipRight={-(offsetContainerWidth / 10 + 0.3)}
              pointHorizontalOffset={offsetContainerWidth / 10 + 0.3}
              width={26}
              borderRadius={0.4}
            >
              <div className="content">
                <div className="text">결제 예상 금액</div>
                <div className="tooltip-icon">
                  <Icon_help size={1.5} unit="rem" />
                </div>
              </div>
            </FixedTooltip>
          </TooltipWrapper>
          <div ref={offsetContainerRef} className="total-price">
            {toKRW(price)}
          </div>
        </PriceAmountWrapper>
      </DescContainer>

      {totalMemberDiscountPriceText && (
        <VipDiscountNotice totalDiscountPriceText={totalMemberDiscountPriceText} />
      )}

      <ButtonWrapper>
        <Button
          size={BUTTON_SIZE.XL}
          title={'구매하기'}
          isFullSize={true}
          type={BUTTON_TYPE.PRIMARY}
          isDisabled={disabled}
          onClick={() => onOrderClick?.()}
          onDisabledClick={() => onDisabledOrderClick?.()}
          testId={'cart-order-button'}
        />
      </ButtonWrapper>
    </div>
  )
}

export default CartPaymentButton
